import React from 'react';
import { Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ExperimentOutlined,
  ApartmentOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import styles from './CourseNavigationModal.module.css';

/**
 * CourseNavigationModal - Interactive Course Table of Contents
 *
 * Displays the complete A-Z Heat Pump Course structure in a modal dialog.
 * Allows navigation to any section or subsection from anywhere in the course.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Array} props.courseStructure - Array of course sections with subsections
 * @param {string} [props.currentPath] - Current route path to highlight active section
 *
 * @returns {JSX.Element} Modal with navigable course structure
 *
 * @example
 * <CourseNavigationModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   courseStructure={courseStructure}
 *   currentPath="/az-course/heat-loss"
 * />
 */
export function CourseNavigationModal({ open, onClose, courseStructure, currentPath }) {
  const navigate = useNavigate();

  /**
   * Handle navigation to a subsection
   * Closes modal and navigates to the selected link
   */
  const handleNavigate = (link) => {
    if (link) {
      onClose();
      navigate(link);
    }
  };

  /**
   * Get icon component based on section ID
   */
  const getIcon = (sectionId) => {
    const iconMap = {
      'cold-room': <ExperimentOutlined />,
      'heat-input': <ApartmentOutlined />,
      'two-room-scenarios': <ApartmentOutlined />,
      'case-studies': <FileSearchOutlined />,
    };
    return iconMap[sectionId] || <ExperimentOutlined />;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      className={styles.modal}
      title={
        <div className={styles.modalTitle}>
          <span>A-Z Heat Pump Course</span>
          <span className={styles.modalSubtitle}>Navigate to any section</span>
        </div>
      }
    >
      <div className={styles.modalContent}>
        {courseStructure.map((section) => (
          <div key={section.id} className={styles.section}>
            {/* Section Header */}
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>{getIcon(section.id)}</div>
              <div className={styles.sectionInfo}>
                <h3 className={styles.sectionTitle}>
                  <span className={styles.sectionNumber}>{section.number}.</span>
                  {section.title}
                </h3>
                <p className={styles.sectionDescription}>{section.description}</p>
              </div>
            </div>

            {/* Subsections Grid */}
            <div className={styles.subsectionsGrid}>
              {section.subsections.map((sub) => {
                const isActive = currentPath === sub.link;
                return (
                  <div
                    key={sub.id}
                    className={`${styles.subsectionCard} ${isActive ? styles.active : ''}`}
                    onClick={() => handleNavigate(sub.link)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleNavigate(sub.link)}
                  >
                    <div className={styles.subsectionHeader}>
                      <span className={styles.subsectionId}>{sub.id}</span>
                      {isActive && <span className={styles.activeBadge}>Current</span>}
                    </div>
                    <h4 className={styles.subsectionTitle}>{sub.title}</h4>
                    <p className={styles.subsectionDescription}>{sub.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default CourseNavigationModal;
